import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatDate, formatStatus, getStatusColor } from "@/lib/formatters";
import { Share2 } from "lucide-react";
import { shareNative, getShareMessage, copyToClipboard } from "@/lib/socialShare";
import { useToast } from "@/hooks/use-toast";

interface GiftCardProps {
  amount: number;
  currency: string;
  message: string;
  status: string;
  created_at: string;
  isAnonymous: boolean;
  userName?: string;
  userAvatar?: string;
  type: "sent" | "received";
  netAmount?: number;
  commissionAmount?: number;
  purpose?: string;
}

const GiftCard = ({
  amount,
  currency,
  message,
  status,
  created_at,
  isAnonymous,
  userName,
  userAvatar,
  type,
  netAmount,
  commissionAmount,
  purpose,
}: GiftCardProps) => {
  const { toast } = useToast();
  const displayName = isAnonymous ? "Anonymous" : userName || "Unknown";
  const initials = displayName.slice(0, 2).toUpperCase();

  const handleShare = async () => {
    const shareMessage = getShareMessage(type);
    const success = await shareNative(shareMessage);
    if (!success) {
      await copyToClipboard(shareMessage);
      toast({ title: "Copied!", description: "Share message copied to clipboard" });
    }
  };
  const getPurposeDisplay = (purpose?: string) => {
    const purposes: Record<string, string> = {
      'tip': '💰 Tip',
      'street_performance': '🎭 Street Performance',
      'busking': '🎸 Busking',
      'kindness': '💝 Random Kindness',
      'help': '🤝 Help',
      'appreciation': '🙏 Appreciation',
      'celebration': '🎉 Celebration',
      'other': '✨ Other',
    };
    return purposes[purpose || ''] || null;
  };

  return (
    <div className="p-4 rounded-lg bg-card border border-border/50 shadow-sm space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 flex-1">
          <Avatar className="w-10 h-10">
            {!isAnonymous && userAvatar && <AvatarImage src={userAvatar} />}
            <AvatarFallback className="bg-primary/10 text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">
              {type === "sent" ? "To" : "From"} {displayName}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatDate(created_at)}
            </div>
          </div>
        </div>
        <Badge className={getStatusColor(status)} variant="secondary">
          {formatStatus(status)}
        </Badge>
      </div>

      {purpose && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {getPurposeDisplay(purpose)}
          </Badge>
        </div>
      )}

      <p className="text-sm text-muted-foreground line-clamp-2">{message}</p>

      <div className="flex items-center justify-between pt-2 border-t border-border/30">
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">
            {type === "received" ? "Gross received" : "Total paid"}
          </div>
          <div className="text-lg font-bold text-primary">
            {formatCurrency(amount, currency)}
          </div>
        </div>
        
        {type === "received" && netAmount !== undefined && (
          <div className="text-right space-y-1">
            <div className="text-xs text-muted-foreground">Net (you receive)</div>
            <div className="text-sm font-semibold text-green-600">
              {formatCurrency(netAmount, currency)}
            </div>
          </div>
        )}
        
        {type === "sent" && commissionAmount !== undefined && commissionAmount > 0 && (
          <div className="text-right space-y-1">
            <div className="text-xs text-muted-foreground">Platform fee</div>
            <div className="text-sm font-semibold text-muted-foreground">
              {formatCurrency(commissionAmount, currency)}
            </div>
          </div>
        )}
      </div>

      {status === "completed" && (
        <div className="pt-2 border-t border-border/30">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleShare}
            className="text-muted-foreground w-full"
          >
            <Share2 className="w-4 h-4 mr-1" />
            Share
          </Button>
        </div>
      )}
    </div>
  );
};

export default GiftCard;
